import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, Loader2, ListChecks } from "lucide-react";

const STATUS_MAP = {
  pending_kyc: { c: "bg-yellow-500/20 text-yellow-400", t: "KYC Pending" },
  awaiting_payment: { c: "bg-blue-500/20 text-blue-400", t: "Awaiting Payment" },
  payment_done: { c: "bg-purple-500/20 text-purple-400", t: "Payment Done" },
  completed: { c: "bg-green-500/20 text-green-400", t: "Completed" },
  cancelled: { c: "bg-red-500/20 text-red-400", t: "Cancelled" },
  disputed: { c: "bg-orange-500/20 text-orange-400", t: "Disputed" },
};

const P2PMyTradesPage = () => {
  const { user, authAxios } = useAuth();
  const navigate = useNavigate();
  const api = authAxios();
  const meId = user?.id;

  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const r = await api.get("/p2p/trades/my");
      setTrades(r.data.trades || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTrades();
    // eslint-disable-next-line
  }, []);

  const filtered = trades.filter((t) => {
    if (filter === "all") return true;
    if (filter === "active") return ["pending_kyc", "awaiting_payment", "payment_done", "disputed"].includes(t.status);
    if (filter === "completed") return t.status === "completed";
    if (filter === "cancelled") return t.status === "cancelled";
    return true;
  });

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center px-4 py-3">
          <button onClick={() => navigate("/p2p")} className="p-1 mr-2" data-testid="my-trades-back-btn">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold flex items-center gap-2">
            <ListChecks size={20} className="text-primary" /> My Trades
          </h1>
        </div>
        <div className="flex border-t border-border overflow-x-auto">
          {["all", "active", "completed", "cancelled"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`flex-1 min-w-[80px] py-2 text-xs font-semibold ${filter === f ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`}
              data-testid={`filter-${f}`}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">No trades yet</div>
        ) : (
          filtered.map((t) => {
            const isBuyer = t.buyer_id === meId;
            const s = STATUS_MAP[t.status] || { c: "bg-muted", t: t.status };
            return (
              <div
                key={t.id}
                onClick={() => navigate(`/p2p/trade/${t.id}`)}
                className="bg-card border border-border rounded-xl p-3 cursor-pointer hover:border-primary/50 transition space-y-1.5"
                data-testid={`trade-row-${t.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-muted-foreground">#{t.id.slice(0, 8)}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.c}`}>{s.t}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold">
                    {isBuyer ? "BUY" : "SELL"} {t.amount_usdt} USDT
                  </span>
                  <span className="text-primary font-bold">
                    {t.amount_fiat.toFixed(2)} {t.fiat}
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {new Date(t.created_at).toLocaleString()} • {t.country.toUpperCase()}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default P2PMyTradesPage;
